import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { WAR_ROOM, MENU_GROUPS, isPathActive, findGroupKey } from './adminMenu';

const SubLink = ({ item, active }) => {
  const Icon = item.icon;
  return (
    <Link to={item.path} data-testid={`admin-nav-${item.path.split('/').pop()}`}
      className={`group flex items-center gap-2.5 pl-9 pr-3 py-2 rounded-md text-[13px] transition-colors duration-150 ${
        active ? 'bg-orange-500/15 text-orange-300' : 'text-gray-400 hover:text-white hover:bg-white/5'
      }`}>
      <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-orange-400' : 'text-gray-500 group-hover:text-gray-300'}`} />
      <span className="truncate">{item.label}</span>
      {item.basic && <span className="ml-auto text-[9px] uppercase tracking-wider text-gray-500 border border-gray-700 rounded px-1">Básico</span>}
    </Link>
  );
};

const Group = ({ group, open, onToggle, pathname, expanded }) => {
  const Icon = group.icon;
  const hasActive = group.items.some(i => isPathActive(pathname, i.path));
  return (
    <li>
      <button type="button" onClick={onToggle} title={!expanded ? group.label : ''} data-testid={`admin-nav-group-${group.key}`}
        aria-expanded={open}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-150 ${
          hasActive ? 'text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
        } ${!expanded ? 'justify-center' : ''}`}>
        <Icon className={`w-5 h-5 shrink-0 ${hasActive ? 'text-orange-400' : ''}`} />
        {expanded && (
          <>
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-left flex-1">{group.label}</span>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>
      {expanded && (
        <div className={`grid transition-[grid-template-rows] duration-200 ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
          <div className="overflow-hidden">
            <div className="py-1 space-y-0.5" data-testid={`admin-nav-group-${group.key}-items`}>
              {open && group.items.map(item => <SubLink key={item.path} item={item} active={isPathActive(pathname, item.path)} />)}
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

export const AdminSidebarNav = ({ expanded, onExpand }) => {
  const { pathname } = useLocation();
  const [openKey, setOpenKey] = useState(() => findGroupKey(pathname));

  useEffect(() => { setOpenKey(findGroupKey(pathname)); }, [pathname]);

  const toggle = (key) => {
    if (!expanded) { onExpand(); setOpenKey(key); return; }
    setOpenKey(k => (k === key ? null : key));
  };

  const warActive = isPathActive(pathname, WAR_ROOM.path);
  const WarIcon = WAR_ROOM.icon;

  return (
    <nav className="flex-1 py-4 overflow-y-auto" data-testid="admin-sidebar-nav">
      <ul className="space-y-1 px-2">
        <li>
          <Link to={WAR_ROOM.path} title={!expanded ? WAR_ROOM.label : ''} data-testid="admin-nav-warroom-dashboard"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-150 mb-2 ${
              warActive ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' : 'text-gray-300 hover:bg-white/5 hover:text-white'
            } ${!expanded ? 'justify-center' : ''}`}>
            <WarIcon className="w-5 h-5 shrink-0" />
            {expanded && <span className="text-[11px] font-bold uppercase tracking-[0.12em]">{WAR_ROOM.label}</span>}
          </Link>
        </li>
        {MENU_GROUPS.map(g => (
          <Group key={g.key} group={g} open={openKey === g.key} onToggle={() => toggle(g.key)} pathname={pathname} expanded={expanded} />
        ))}
      </ul>
    </nav>
  );
};
